// Shared helpers for the Project Timeline tab (timeline/ and schedule/ components).
// Dates from the timelines/schedule endpoints are 'YYYY-MM-DD' strings, so day comparisons are plain string compares.
import dayjs from 'dayjs';

export const UNASSIGNED = 'unassigned';
export const LONG_TASK_DAYS = 3;
export const BUSY_DAY_TASKS = 6;

const SITE_PALETTE = ['#2563eb', '#db2777', '#7c3aed', '#ea580c', '#059669', '#0891b2', '#ca8a04', '#be123c', '#4f46e5', '#16a34a'];

// Stable colour per facility so a site keeps its colour across months and views
export const siteColor = (facilityId) => {
    const key = String(facilityId || '');
    const hash = Array.from(key).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 100000, 7);
    return SITE_PALETTE[hash % SITE_PALETTE.length];
};

export const SUB_TASK_STATUS_STYLES = {
    'Pending':     { color: '#475569', bgcolor: '#f1f5f9' },
    'In Progress': { color: '#008080', bgcolor: '#e6f4f2' },
    'On Hold':     { color: '#8f6d1d', bgcolor: '#fffdee' },
    'Completed':   { color: '#196a3e', bgcolor: '#f1faf4' },
};
export const subTaskStatusStyle = (status) => SUB_TASK_STATUS_STYLES[status] || SUB_TASK_STATUS_STYLES.Pending;

// Copied from BusinessDashboard/tabs/ProjectManagement.js (that file is left untouched)
export const getProjectStatusStyle = (status) => {
    switch (status) {
        case 'Completed': return { color: '#196a3e', bgcolor: '#f1faf4' };
        case 'Active':    return { color: '#1a6894', bgcolor: '#f1f8fc' };
        case 'Delayed':   return { color: '#c62828', bgcolor: '#ffebee' };
        default:          return { color: '#757575', bgcolor: '#f5f5f5' };
    }
};

export const todayYmd = () => dayjs().format('YYYY-MM-DD');

export const fmtDate = (ymd, format = 'ddd D MMM') => (ymd ? dayjs(ymd).format(format) : 'TBD');

export const fmtRange = (start, end, format = 'D MMM') => {
    if (!start && !end) return 'Dates TBD';
    if (start === end) return fmtDate(start, format);
    return `${fmtDate(start, format)} – ${fmtDate(end, format)}`;
};

export const taskLength = (t) => dayjs(t.end_date).diff(dayjs(t.start_date), 'day') + 1;

export const isOnDay = (t, ymd) => t.start_date <= ymd && t.end_date >= ymd;

export const dayNumber = (t, ymd) => dayjs(ymd).diff(dayjs(t.start_date), 'day') + 1;

// How a task appears on a given day: short tasks show every day, long ones only on their first and last day
export const dayRole = (t, ymd) => {
    if (taskLength(t) <= 2) return 'one';
    if (ymd === t.start_date) return 'start';
    if (ymd === t.end_date) return 'end';
    return 'mid';
};

export const monthDays = (year, month) => {
    const first = dayjs().year(year).month(month).date(1);
    return Array.from({ length: first.daysInMonth() }, (_, i) => first.date(i + 1).format('YYYY-MM-DD'));
};

// Sunday-first weeks covering the month, padded with days from the neighbouring months
export const monthWeeks = (year, month) => {
    const first = dayjs().year(year).month(month).date(1);
    const start = first.subtract(first.day(), 'day');
    const cells = Math.ceil((first.day() + first.daysInMonth()) / 7) * 7;
    const weeks = [];
    for (let i = 0; i < cells; i++) {
        const d = start.add(i, 'day');
        if (i % 7 === 0) weeks.push([]);
        weeks[weeks.length - 1].push({ ymd: d.format('YYYY-MM-DD'), day: d.date(), inMonth: d.month() === month, weekday: d.day() });
    }
    return weeks;
};

export const engineerKey = (t) => (t.spoc ? (t.spoc.id || t.spoc.name) : UNASSIGNED);
export const engineerName = (t) => (t.spoc ? (t.spoc.name || t.spoc.id) : 'Unassigned');
export const isUnassigned = (t) => !t.spoc;

export const initials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '!';
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// [{facility_id, facility_name, tasks}] in first-seen order
export const groupBySite = (tasks) => {
    const map = new Map();
    tasks.forEach(t => {
        if (!map.has(t.facility_id)) map.set(t.facility_id, { facility_id: t.facility_id, facility_name: t.facility_name, tasks: [] });
        map.get(t.facility_id).tasks.push(t);
    });
    return Array.from(map.values());
};

export const projectLabel = (p) => (p ? `${p.facility_name} · ${p.description || `Project #${p.id}`}` : '');

export const stepProgressLabel = (project) => {
    const steps = project?.steps || [];
    const nowIdx = steps.findIndex(s => s.status === 'now' || s.status === 'on_hold');
    const current = nowIdx >= 0 ? steps[nowIdx] : null;
    const next = steps.find(s => s.status === 'upcoming');
    const done = steps.filter(s => s.status === 'done').length;
    const stepNo = current ? nowIdx + 1 : Math.min(steps.length, done + (next ? 1 : 0));
    return { current, next, stepNo: Math.max(1, stepNo), total: steps.length };
};

// Client calendar: lay one week's tasks out as bars. Each bar spans its days within the week and takes the
// first free lane; past maxLanes, the days are counted in `more` instead. clipLeft/clipRight mark bars
// that continue from the previous week or into the next one.
export const packWeek = (tasks, week, maxLanes = 3) => {
    const ws = week[0].ymd;
    const we = week[6].ymd;
    const col = (ymd) => week.findIndex(c => c.ymd === ymd);
    const segments = tasks
        .filter(t => t.start_date <= we && t.end_date >= ws)
        .map(t => ({
            task: t,
            from: col(t.start_date < ws ? ws : t.start_date),
            to: col(t.end_date > we ? we : t.end_date),
            clipLeft: t.start_date < ws,
            clipRight: t.end_date > we,
        }))
        .sort((a, b) => a.from - b.from || (b.to - b.from) - (a.to - a.from));
    const laneEnds = [];
    const placed = [];
    const more = Array(7).fill(0);
    segments.forEach(seg => {
        let lane = laneEnds.findIndex(end => end < seg.from);
        if (lane < 0) { lane = laneEnds.length; laneEnds.push(-1); }
        if (lane >= maxLanes) { for (let c = seg.from; c <= seg.to; c++) more[c]++; return; }
        laneEnds[lane] = seg.to;
        placed.push({ ...seg, lane });
    });
    return { placed, more };
};

// ── Site visits (check-in/out) ──
export const fmtTime = (iso) => (iso ? dayjs(iso).format('h:mm a') : '—');

// Minutes on site; an open visit counts up to now
export const visitMinutes = (v) => {
    if (!v?.check_in_at) return 0;
    const end = v.check_out_at ? dayjs(v.check_out_at) : dayjs();
    return Math.max(0, end.diff(dayjs(v.check_in_at), 'minute'));
};

export const fmtDuration = (minutes) => {
    const m = Math.max(0, Math.round(minutes));
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};

export const isOpenVisit = (v) => !!v?.check_in_at && !v?.check_out_at;

// Schedule items are sub-tasks (key "project:<id>") or ticket visits ("ticket:<id>"); visits carry the same key
export const itemKey = (t) => t.key || `project:${t.id}`;
export const visitKey = (v) => v.item_key || `project:${v.sub_task_id}`;
export const isTicketItem = (t) => t?.kind === 'ticket';

// Visits for one schedule item on one day (one per engineer)
export const visitsFor = (visits, item, ymd) => visits.filter(v => visitKey(v) === itemKey(item) && v.visit_date === ymd);

// Saves a file sent by the server as base64 (schedule and budget PDFs)
export const saveBase64File = (base64, fileName, mime = 'application/pdf') => {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([bytes], { type: mime }));
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};
